import type { Communication, Reference } from '@medplum/fhirtypes';
import { getProfileReference } from './messagingFhir';

/**
 * Canonical refs used in `data/mockMessagingData.ts`. `@medplum/mock` often seeds
 * `Patient/123` and `Practitioner/123`; we map those to this dataset so own/other styling works.
 */
export const MOCK_MESSAGING = {
  PATIENT_JANE: { reference: 'Patient/patient-456', display: 'Jane Doe' } as Reference,
  PRACTITIONER_SMITH: { reference: 'Practitioner/practitioner-789', display: 'Dr. Smith' } as Reference,
} as const;

type ProfileLike = { resourceType: string; id?: string } | undefined;

/** Live thread search/recipient is only defined for human participants (not OAuth ClientApplication, Bot, etc.). */
export function isMessagingParticipantProfile(profile: ProfileLike): boolean {
  return profile?.resourceType === 'Patient' || profile?.resourceType === 'Practitioner';
}

/**
 * Resolves the signed-in user reference for messaging (own vs other bubbles, compose sender).
 * In mock mode, aligns Medplum MockClient profile ids with the static mock message senders.
 */
export function resolveMessagingCurrentUserRef(profile: ProfileLike, isMock: boolean): Reference {
  if (!isMock) {
    if (!profile?.id) {
      return { reference: 'Practitioner/unknown', display: 'You' };
    }
    return getProfileReference(profile);
  }

  if (!profile?.id) {
    return MOCK_MESSAGING.PRACTITIONER_SMITH;
  }

  const { resourceType: rt, id } = profile;
  if (rt === 'Patient') {
    if (id === '123') {
      return MOCK_MESSAGING.PATIENT_JANE;
    }
    return getProfileReference(profile);
  }
  if (rt === 'Practitioner') {
    if (id === '123') {
      return MOCK_MESSAGING.PRACTITIONER_SMITH;
    }
    return getProfileReference(profile);
  }

  return getProfileReference(profile);
}

/** Whether a message was sent by the current user (for bubble alignment and metadata). */
export function isMessageFromCurrentUser(
  message: Pick<Communication, 'sender'>,
  currentUserRef: Reference | string,
): boolean {
  const me = typeof currentUserRef === 'string' ? currentUserRef : currentUserRef.reference;
  const senderRef = message.sender?.reference;
  if (me && senderRef && senderRef === me) {
    return true;
  }
  if (message.sender?.display === 'You') {
    return true;
  }
  if (senderRef?.includes('current-user')) {
    return true;
  }
  return false;
}
